import { useState, useEffect } from 'react';
import { MapPin, Percent, Truck, Save, Info, Map, Search, Gift, MessageSquare, Globe, Check, AlertTriangle, Zap, Package, ToggleLeft, ToggleRight, Trash2, ExternalLink } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import LocationSearch from '../../components/LocationSearch';
import WhatsAppSettings from '../../components/WhatsAppSettings';

export default function StoreSettings() {
    const { user, hasFeatureAccess } = useAuth();
    const [settings, setSettings] = useState({
        name: '',
        address: '',
        latitude: '',
        longitude: '',
        gst_percentage: 5.00,
        base_delivery_charge: 0.00,
        delivery_charge_per_km: 0.00,
        is_free_delivery: false,
        free_delivery_threshold: 0.00,
        delivery_radius: 10.00,
        contact_submission_email: ''
    });
    const [chatbotConfig, setChatbotConfig] = useState({
        provider: 'disabled',
        widgetId: ''
    });
    const [subdomainSlug, setSubdomainSlug] = useState('');
    const [customDomain, setCustomDomain] = useState('');
    const [domainVerified, setDomainVerified] = useState(false);
    const [dnsError, setDnsError] = useState('');
    const [savingDomain, setSavingDomain] = useState(false);
    const [loading, setLoading] = useState(true);
    const [savingIdentity, setSavingIdentity] = useState(false);
    const [savingDelivery, setSavingDelivery] = useState(false);
    const [savingChatbot, setSavingChatbot] = useState(false);
    const [showManualCoords, setShowManualCoords] = useState(false);
    const [deliveryMode, setDeliveryMode] = useState('own');
    const [defaultDeliveryProvider, setDefaultDeliveryProvider] = useState('');
    const [adloggsConfig, setAdloggsConfig] = useState({
        apiKey: '',
        partnerMerchantId: ''
    });
    const [adloggsConnected, setAdloggsConnected] = useState(false);
    const [savingDeliveryMode, setSavingDeliveryMode] = useState(false);
    const [savingAdloggs, setSavingAdloggs] = useState(false);
    const toast = useToast();

    // Check if current user role has permission to configure chatbot support (Professional or Premium plans)
    // Professional plan is allowed 'customer_app' / 'admin_app' download
    const canConfigureChatbot = user?.plan_name === 'Professional' || user?.plan_name === 'Premium';

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const [opRes, hpRes, domainRes] = await Promise.all([
                api.get('/api/restaurants/settings/operational'),
                api.get('/api/restaurants/settings/homepage').catch(() => ({ data: {} })),
                api.get('/api/restaurants/settings/domain').catch(() => ({ data: {} }))
            ]);

            if (opRes.data) {
                setSettings({
                    name: opRes.data.name || '',
                    address: opRes.data.address || '',
                    latitude: opRes.data.latitude || '',
                    longitude: opRes.data.longitude || '',
                    gst_percentage: opRes.data.gst_percentage || 5.00,
                    base_delivery_charge: opRes.data.base_delivery_charge || 0.00,
                    delivery_charge_per_km: opRes.data.delivery_charge_per_km || 0.00,
                    is_free_delivery: opRes.data.is_free_delivery || false,
                    free_delivery_threshold: opRes.data.free_delivery_threshold || 0.00,
                    delivery_radius: opRes.data.delivery_radius || 10.00,
                    contact_submission_email: opRes.data.contact_submission_email || ''
                });
                setDeliveryMode(opRes.data.delivery_mode || 'own');
                setDefaultDeliveryProvider(opRes.data.default_delivery_provider || '');
            }

            // Fetch Adloggs config
            try {
                const deliveryConfigRes = await api.get('/api/external-delivery/config');
                if (deliveryConfigRes.data && deliveryConfigRes.data.length > 0) {
                    const adloggsConf = deliveryConfigRes.data.find(c => c.provider === 'adloggs');
                    if (adloggsConf) {
                        setAdloggsConfig({
                            apiKey: adloggsConf.apiKey || '',
                            partnerMerchantId: adloggsConf.partnerMerchantId || ''
                        });
                        setAdloggsConnected(adloggsConf.is_active);
                    }
                }
            } catch (err) {
                console.log('No delivery config found');
            }

            if (hpRes.data && hpRes.data.chatbot_config) {
                setChatbotConfig({
                    provider: hpRes.data.chatbot_config.provider || 'disabled',
                    widgetId: hpRes.data.chatbot_config.widgetId || ''
                });
            }

            if (domainRes.data) {
                setSubdomainSlug(domainRes.data.slug || '');
                if (domainRes.data.custom_frontend_url) {
                    setCustomDomain(domainRes.data.custom_frontend_url);
                    setDomainVerified(true); // Treat as verified if already saved, will re-verify on save/check
                }
            }
        } catch (error) {
            console.error('Fetch settings error:', error);
            toast.error('Failed to load restaurant settings');
        } finally {
            setLoading(false);
        }
    };

    const handleLocationSelect = (location) => {
        if (location) {
            setSettings(prev => ({
                ...prev,
                address: location.address,
                latitude: location.lat,
                longitude: location.lng
            }));
        }
    };

    const saveDeliveryMode = async () => {
        setSavingDeliveryMode(true);
        try {
            await api.post('/api/restaurants/settings/operational', {
                ...settings,
                delivery_mode: deliveryMode,
                default_delivery_provider: deliveryMode !== 'own' ? 'adloggs' : null
            });
            setDefaultDeliveryProvider(deliveryMode !== 'own' ? 'adloggs' : '');
            toast.success('Delivery mode updated successfully');
        } catch (err) {
            toast.error('Failed to update delivery mode');
        } finally {
            setSavingDeliveryMode(false);
        }
    };

    const saveAdloggsConfig = async () => {
        if (!adloggsConfig.apiKey) {
            toast.error('API Key is required');
            return;
        }
        setSavingAdloggs(true);
        try {
            await api.post('/api/external-delivery/config', {
                provider: 'adloggs',
                apiKey: adloggsConfig.apiKey,
                partnerMerchantId: adloggsConfig.partnerMerchantId
            });
            setAdloggsConnected(true);
            toast.success('Adloggs configuration verified and saved!');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save Adloggs configuration. Check your API Key.');
        } finally {
            setSavingAdloggs(false);
        }
    };

    const removeAdloggsConfig = async () => {
        try {
            await api.delete('/api/external-delivery/config/adloggs');
            setAdloggsConfig({ apiKey: '', partnerMerchantId: '' });
            setAdloggsConnected(false);
            setDeliveryMode('own');
            setDefaultDeliveryProvider('');
            await api.post('/api/restaurants/settings/operational', { ...settings, delivery_mode: 'own', default_delivery_provider: null });
            toast.success('Adloggs configuration removed');
        } catch (err) {
            toast.error('Failed to remove configuration');
        }
    };

    const handleSaveIdentity = async (e) => {
        e.preventDefault();
        setSavingIdentity(true);
        try {
            await api.post('/api/restaurants/settings/operational', {
                ...settings,
                delivery_mode: deliveryMode,
                default_delivery_provider: deliveryMode !== 'own' ? 'adloggs' : null
            });
            toast.success('Store details and map location updated successfully');
            window.dispatchEvent(new Event('configUpdated'));
        } catch (err) {
            toast.error('Failed to update store details');
        } finally {
            setSavingIdentity(false);
        }
    };

    const handleSaveDelivery = async (e) => {
        e.preventDefault();
        setSavingDelivery(true);
        try {
            await api.post('/api/restaurants/settings/operational', {
                ...settings,
                delivery_mode: deliveryMode,
                default_delivery_provider: deliveryMode !== 'own' ? 'adloggs' : null
            });
            setDefaultDeliveryProvider(deliveryMode !== 'own' ? 'adloggs' : '');
            toast.success('Tax, delivery fee policy, and delivery mode updated successfully');
            window.dispatchEvent(new Event('configUpdated'));
        } catch (err) {
            toast.error('Failed to update delivery settings');
        } finally {
            setSavingDelivery(false);
        }
    };

    const handleSaveChatbot = async (e) => {
        e.preventDefault();
        if (!canConfigureChatbot) {
            toast.error('Chatbot integrations are only available on Professional or Premium plans.');
            return;
        }
        setSavingChatbot(true);
        try {
            // Retrieve homepage settings to preserve them
            const hpRes = await api.get('/api/restaurants/settings/homepage');
            const currentHomeSettings = hpRes.data.home_settings || {};
            
            await api.post('/api/restaurants/settings/homepage', {
                home_settings: currentHomeSettings,
                chatbot_config: chatbotConfig.provider === 'disabled' ? null : chatbotConfig
            });
            toast.success('Chatbot configuration updated successfully');
        } catch (err) {
            console.error('Error saving chatbot config:', err);
            toast.error('Failed to update chatbot configuration');
        } finally {
            setSavingChatbot(false);
        }
    };

    const handleSaveDomain = async (e) => {
        e.preventDefault();
        setSavingDomain(true);
        setDnsError('');
        try {
            const res = await api.post('/api/restaurants/settings/domain', { 
                slug: subdomainSlug,
                customDomain 
            });
            if (res.data.slug) {
                setSubdomainSlug(res.data.slug);
            }
            if (res.data.verified) {
                setDomainVerified(true);
                toast.success('Domain settings updated and verified!');
            } else {
                setDomainVerified(false);
                setDnsError(res.data.dnsError || 'DNS propagation may take time. Please double check.');
                if (!customDomain) {
                    toast.success('Subdomain updated successfully. Custom domain removed.');
                } else {
                    toast.warning('Subdomain updated, but custom domain DNS verification failed.');
                }
            }
        } catch (err) {
            console.error('Error saving domain settings:', err);
            toast.error(err.response?.data?.message || 'Failed to update domain settings');
        } finally {
            setSavingDomain(false);
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto space-y-8 pb-12">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Store Configuration</h1>
                <p className="text-gray-500 mt-1 text-sm">Manage your restaurant's location, taxes, and delivery rules.</p>
            </div>

            {/* <WhatsAppSettings /> */}

            <form onSubmit={handleSaveIdentity} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Restaurant Profile Identity */}
                    <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4 md:col-span-2">
                        <div className="flex items-center space-x-2 text-gray-900 font-bold mb-2">
                            <span>Store Identity</span>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-sm font-bold text-gray-700">Restaurant Name</label>
                            <input 
                                type="text"
                                required
                                value={settings.name}
                                onChange={(e) => setSettings({...settings, name: e.target.value})}
                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none text-sm font-semibold"
                                placeholder="e.g. Olive Greenz Restaurant"
                            />
                        </div>
                    </div>
                    {/* Location Settings (Map Based) */}
                    <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4 md:col-span-2">
                        <div className="flex items-center space-x-2 text-gray-900 font-bold mb-2">
                            <Search className="w-5 h-5 text-primary-600" />
                            <span>Locate Store on Maps</span>
                        </div>
                        
                        <div className="bg-gray-50 p-6 rounded-xl border border-dashed border-gray-300">
                            <LocationSearch 
                                onLocationSelect={handleLocationSelect}
                                selectedLocation={{ 
                                    address: settings.address, 
                                    lat: parseFloat(settings.latitude), 
                                    lng: parseFloat(settings.longitude) 
                                }}
                            />
                        </div>

                        <div className="flex items-center justify-between pt-2">
                            <div className="flex items-center space-x-4">
                                <div className="text-xs">
                                    <span className="text-gray-500 mr-1">Lat:</span>
                                    <span className="font-mono font-bold text-gray-900">{settings.latitude || 'Not set'}</span>
                                </div>
                                <div className="text-xs">
                                    <span className="text-gray-500 mr-1">Lng:</span>
                                    <span className="font-mono font-bold text-gray-900">{settings.longitude || 'Not set'}</span>
                                </div>
                            </div>
                            <button 
                                type="button" 
                                onClick={() => setShowManualCoords(!showManualCoords)}
                                className="text-[10px] uppercase tracking-wider font-bold text-primary-600 hover:text-primary-700"
                            >
                                {showManualCoords ? 'Hide Manual Inputs' : 'Edit Manually'}
                            </button>
                        </div>

                        {showManualCoords && (
                            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 animate-in fade-in slide-in-from-top-2 duration-300">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-gray-700">Manual Latitude</label>
                                    <input 
                                        type="number" 
                                        step="any"
                                        value={settings.latitude}
                                        onChange={(e) => setSettings({...settings, latitude: e.target.value})}
                                        className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-gray-700">Manual Longitude</label>
                                    <input 
                                        type="number" 
                                        step="any"
                                        value={settings.longitude}
                                        onChange={(e) => setSettings({...settings, longitude: e.target.value})}
                                        className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Operational Limits & Tax Settings */}
                    <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
                        <div className="flex items-center space-x-2 text-gray-900 font-bold mb-2">
                            <Percent className="w-5 h-5 text-primary-600" />
                            <span>Operational Limits & Tax</span>
                        </div>
                        
                        <div className="space-y-1.5">
                            <label className="text-sm font-bold text-gray-700">GST Percentage (%) *</label>
                            <div className="relative">
                                <input 
                                    type="number" 
                                    step="0.01"
                                    required
                                    value={settings.gst_percentage}
                                    onChange={(e) => setSettings({...settings, gst_percentage: e.target.value})}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none text-sm pr-10"
                                    placeholder="5.00"
                                />
                                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-gray-400">
                                    %
                                </div>
                            </div>
                        </div>

                        <div className="space-y-1.5 pt-2">
                            <label className="text-sm font-bold text-gray-700">Delivery Radius (KM) *</label>
                            <div className="relative">
                                <input 
                                    type="number" 
                                    step="0.01"
                                    required
                                    min="0.1"
                                    value={settings.delivery_radius}
                                    onChange={(e) => setSettings({...settings, delivery_radius: e.target.value})}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none text-sm pr-10"
                                    placeholder="10.00"
                                />
                                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-gray-400 font-bold">
                                    km
                                </div>
                            </div>
                            <p className="text-[10px] text-gray-400 italic">Maximum distance allowed for customer delivery orders.</p>
                        </div>

                        <div className="space-y-1.5 pt-2">
                            <label className="text-sm font-bold text-gray-700">Contact Form Recipient Email</label>
                            <input 
                                type="email" 
                                value={settings.contact_submission_email}
                                onChange={(e) => setSettings({...settings, contact_submission_email: e.target.value})}
                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none text-sm"
                                placeholder="email@example.com"
                            />
                            <p className="text-[10px] text-gray-400 italic">Submissions from the storefront contact form will be emailed here.</p>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end pt-2">
                    <button
                        type="submit"
                        disabled={savingIdentity}
                        className="flex items-center space-x-3 bg-gray-900 text-white px-12 py-4 rounded-xl font-bold hover:bg-gray-800 transition-all disabled:opacity-50 shadow-xl shadow-gray-200 group"
                    >
                        {savingIdentity ? (
                           <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                            <>
                                <Save className="w-5 h-5 transition-transform group-hover:scale-110" />
                                <span>Save Store Details</span>
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Delivery Mode Configuration */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                        <Package className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">Delivery Mode</h2>
                        <p className="text-xs text-gray-500">Choose how your orders are delivered</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {/* Own Delivery */}
                    <button
                        type="button"
                        onClick={() => setDeliveryMode('own')}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                            deliveryMode === 'own'
                                ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                    >
                        <div className="flex items-center gap-2 mb-2">
                            <Truck className={`w-5 h-5 ${deliveryMode === 'own' ? 'text-primary-600' : 'text-gray-400'}`} />
                            <span className={`font-bold text-sm ${deliveryMode === 'own' ? 'text-primary-700' : 'text-gray-700'}`}>Own Delivery</span>
                        </div>
                        <p className="text-xs text-gray-500">Use your own delivery riders to fulfill orders</p>
                    </button>

                    {/* Partner Delivery */}
                    <button
                        type="button"
                        onClick={() => setDeliveryMode('partner')}
                        className={`p-4 rounded-xl border-2 text-left transition-all relative overflow-hidden ${
                            deliveryMode === 'partner'
                                ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <Zap className={`w-5 h-5 ${deliveryMode === 'partner' ? 'text-purple-600' : 'text-gray-400'}`} />
                                <span className={`font-bold text-sm ${deliveryMode === 'partner' ? 'text-purple-700' : 'text-gray-700'}`}>Partner Delivery</span>
                            </div>
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-200 animate-pulse uppercase tracking-wider">
                                In Progress
                            </span>
                        </div>
                        <p className="text-xs text-gray-500">Use Adloggs Delivery for all deliveries</p>
                    </button>
                </div>

                <button
                    onClick={saveDeliveryMode}
                    disabled={savingDeliveryMode || (deliveryMode === 'partner' && !adloggsConnected)}
                    className="flex items-center space-x-2 bg-primary-600 text-white px-6 py-3 rounded-xl font-bold text-sm hover:bg-primary-700 transition-all disabled:opacity-50 shadow-md"
                >
                    <Save className="w-4 h-4" />
                    <span>{savingDeliveryMode ? 'Saving...' : 'Save Delivery Mode'}</span>
                </button>

                {deliveryMode === 'partner' && !adloggsConnected && (
                    <p className="text-xs text-orange-600 mt-2 font-medium">
                        ⚠️ Please configure and save your Adloggs credentials below before selecting this delivery mode.
                    </p>
                )}

                {/* Adloggs Configuration (shown when partner is selected) */}
                {deliveryMode === 'partner' && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Zap className="w-5 h-5 text-indigo-600" />
                                <h3 className="font-bold text-gray-900">Adloggs Configuration</h3>
                                {adloggsConnected && (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full flex items-center gap-1">
                                        <Check className="w-3 h-3" /> Connected
                                    </span>
                                )}
                            </div>
                            {adloggsConnected && (
                                <button
                                    onClick={removeAdloggsConfig}
                                    className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1"
                                >
                                    <Trash2 className="w-3 h-3" /> Remove
                                </button>
                            )}
                        </div>

                        <p className="text-xs text-gray-500 mb-4">
                            Connect your Adloggs account for hyperlocal food delivery across all cities. 
                            <a href="https://adloggs.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline ml-1">
                                Visit Adloggs Portal <ExternalLink className="w-3 h-3 inline" />
                            </a>
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Adloggs API Key (x-api-key)</label>
                                <input
                                    type="text"
                                    value={adloggsConfig.apiKey}
                                    onChange={(e) => setAdloggsConfig(prev => ({ ...prev, apiKey: e.target.value }))}
                                    placeholder="Enter your Adloggs API Key"
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm bg-gray-50"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Partner Merchant ID (Optional)</label>
                                <input
                                    type="text"
                                    value={adloggsConfig.partnerMerchantId}
                                    onChange={(e) => setAdloggsConfig(prev => ({ ...prev, partnerMerchantId: e.target.value }))}
                                    placeholder="Enter Partner Merchant ID"
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm bg-gray-50"
                                />
                            </div>
                        </div>

                        <button
                            onClick={saveAdloggsConfig}
                            disabled={savingAdloggs}
                            className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-bold text-sm flex items-center gap-2 transition-colors disabled:opacity-50 shadow-sm"
                        >
                            <Save className="w-4 h-4" />
                            <span>{savingAdloggs ? 'Verifying & Saving...' : 'Verify & Save Adloggs Config'}</span>
                        </button>
                    </div>
                )}
            </div>

            <form onSubmit={handleSaveDelivery} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    {/* Delivery Toggle Settings */}
                    <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
                        <div className="flex items-center space-x-2 text-gray-900 font-bold mb-2">
                            <Gift className="w-5 h-5 text-primary-600" />
                            <span>Delivery Preference</span>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                                <div className="space-y-0.5">
                                    <p className="text-sm font-bold text-gray-900">Always Free Delivery</p>
                                    <p className="text-[10px] text-gray-500">Offer free delivery for all orders regardless of distance.</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        className="sr-only peer"
                                        checked={settings.is_free_delivery}
                                        onChange={(e) => setSettings({...settings, is_free_delivery: e.target.checked})}
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                </label>
                            </div>

                            <div className={`space-y-1.5 transition-all duration-300 ${settings.is_free_delivery ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
                                <label className="text-sm font-bold text-gray-700">Free Delivery Threshold (₹)</label>
                                <div className="relative">
                                     <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400 font-bold p-1">
                                        ₹
                                    </div>
                                    <input 
                                        type="number" 
                                        step="0.01"
                                        disabled={settings.is_free_delivery}
                                        value={settings.free_delivery_threshold}
                                        onChange={(e) => setSettings({...settings, free_delivery_threshold: e.target.value})}
                                        className="w-full pl-8 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none text-sm"
                                        placeholder="0.00"
                                    />
                                </div>
                                <p className="text-[10px] text-gray-400 italic">Free delivery if order total exceeds this amount (set 0 to disable).</p>
                            </div>
                        </div>
                    </div>

                    {/* Delivery Calculation Settings */}
                    <div className={`bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4 md:col-span-2 transition-all duration-300 ${settings.is_free_delivery ? 'opacity-40 pointer-events-none grayscale' : 'opacity-100'}`}>
                        <div className="flex items-center space-x-2 text-gray-900 font-bold mb-2">
                            <Truck className="w-5 h-5 text-primary-600" />
                            <span>Delivery Fee Rules</span>
                        </div>
                        
                        <div className={deliveryMode === 'partner' ? 'opacity-50 pointer-events-none' : ''}>
                            {deliveryMode === 'partner' && (
                                <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                    <p className="text-xs text-purple-700 font-medium flex items-center gap-1">
                                        <Info className="w-3.5 h-3.5" />
                                        Delivery fees are managed by Adloggs when Partner Delivery mode is active.
                                    </p>
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700">Base Delivery Fee (₹)</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400 font-bold p-1">
                                        ₹
                                    </div>
                                    <input 
                                        type="number" 
                                        step="0.01"
                                        disabled={settings.is_free_delivery}
                                        value={settings.base_delivery_charge}
                                        onChange={(e) => setSettings({...settings, base_delivery_charge: e.target.value})}
                                        className="w-full pl-8 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none text-sm"
                                    />
                                </div>
                                <p className="text-[10px] text-gray-400">Fixed starting fee for all delivery orders.</p>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700">Per KM Charge (₹)</label>
                                <div className="relative">
                                     <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400 font-bold p-1">
                                        ₹
                                    </div>
                                    <input 
                                        type="number" 
                                        step="0.01"
                                        disabled={settings.is_free_delivery}
                                        value={settings.delivery_charge_per_km}
                                        onChange={(e) => setSettings({...settings, delivery_charge_per_km: e.target.value})}
                                        className="w-full pl-8 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none text-sm"
                                    />
                                </div>
                                <p className="text-[10px] text-gray-400 italic">Added fee multiplied by distance from store.</p>
                            </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end pt-4">
                    <button
                        type="submit"
                        disabled={savingDelivery}
                        className="flex items-center space-x-3 bg-gray-900 text-white px-12 py-4 rounded-xl font-bold hover:bg-gray-800 transition-all disabled:opacity-50 shadow-xl shadow-gray-200 group"
                    >
                        {savingDelivery ? (
                           <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                            <>
                                <Save className="w-5 h-5 transition-transform group-hover:scale-110" />
                                <span>Save Tax & Delivery Policy</span>
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Chatbot Configurations Management Panel */}
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-6">
                <div className="flex items-center space-x-3 border-b border-gray-100 pb-4">
                    <div className="bg-primary-50 p-2 rounded-xl text-primary-600">
                        <MessageSquare className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">Live Chatbot Settings</h3>
                        <p className="text-xs text-gray-500">Provide instant live support or configure FAQs for your customers.</p>
                    </div>
                </div>

                {!canConfigureChatbot ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs font-semibold">
                        ⚠️ Chatbot integrations are restricted to <span className="underline">Professional</span> or <span className="underline">Premium</span> plans. Please upgrade your active subscription to unlock this feature.
                    </div>
                ) : (
                    <form onSubmit={handleSaveChatbot} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700">Chatbot Provider</label>
                                <select 
                                    value={chatbotConfig.provider}
                                    onChange={(e) => setChatbotConfig({ ...chatbotConfig, provider: e.target.value })}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none text-sm font-medium"
                                >
                                    <option value="disabled">Disabled</option>
                                    <option value="tidio">Tidio Chatbot</option>
                                    <option value="tawkto">Tawk.to Live Chat</option>
                                </select>
                            </div>

                            {chatbotConfig.provider !== 'disabled' && (
                                <div className="space-y-1.5 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <label className="text-sm font-bold text-gray-700">
                                        {chatbotConfig.provider === 'tidio' ? 'Tidio Public Widget Key' : 'Tawk.to Property ID / Widget ID'}
                                    </label>
                                    <input 
                                        type="text"
                                        required
                                        placeholder={chatbotConfig.provider === 'tidio' ? 'e.g. ufb6bdlk5pytal8uujtcbq2yasrqwmde' : 'e.g. 6a572eb4940f101d5323903b/1jti8ssi9'}
                                        value={chatbotConfig.widgetId}
                                        onChange={(e) => setChatbotConfig({ ...chatbotConfig, widgetId: e.target.value.trim() })}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none text-sm font-mono"
                                    />
                                    <p className="text-[10px] text-gray-400">
                                        {chatbotConfig.provider === 'tidio' 
                                            ? 'Enter the alphanumeric ID from Tidio: code.tidio.co/YOUR_KEY.js' 
                                            : 'Enter the Property ID or Property ID/Widget ID from Tawk.to (e.g. 6a572eb4940f101d5323903b/1jti8ssi9)'}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end pt-2">
                            <button
                                type="submit"
                                disabled={savingChatbot}
                                className="flex items-center space-x-2 bg-primary-600 text-white px-8 py-3 rounded-xl font-bold text-xs hover:bg-primary-700 transition-all disabled:opacity-50 shadow-md"
                            >
                                {savingChatbot ? (
                                   <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        <span>Save Chatbot Integration</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>

            {/* Custom Domain & Subdomain Mapping Panel */}
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-6">
                <div className="flex items-center space-x-3 border-b border-gray-100 pb-4">
                    <div className="bg-primary-50 p-2 rounded-xl text-primary-600">
                        <Globe className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">Domain Configurations</h3>
                        <p className="text-xs text-gray-500">Configure your store subdomain on Aaharam Cloud and connect your custom domain brand mappings.</p>
                    </div>
                </div>

                <form onSubmit={handleSaveDomain} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Subdomain Slug Configuration */}
                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-750 flex items-center gap-1.5">
                                Subdomain Slug
                            </label>
                            <div className="relative flex items-center">
                                <input 
                                    type="text"
                                    required
                                    placeholder="your-restaurant-slug"
                                    value={subdomainSlug}
                                    onChange={(e) => setSubdomainSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none text-sm font-semibold"
                                />
                            </div>
                            <p className="text-[10px] text-gray-400">
                                Your Aaharam Cloud link: <span className="font-mono text-primary-600 font-bold hover:underline">https://{subdomainSlug || 'slug'}.aaharamcloud.com</span>
                            </p>
                        </div>

                        {/* Custom Domain Mapping */}
                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-750">Custom Brand Domain</label>
                            <input 
                                type="text"
                                placeholder="e.g. savorybites.com"
                                value={customDomain}
                                onChange={(e) => setCustomDomain(e.target.value)}
                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none text-sm font-mono"
                            />
                            <p className="text-[10px] text-gray-400">
                                Enter your custom registered domain name (without https or www).
                            </p>
                        </div>
                    </div>

                    <div className="flex justify-end border-t border-gray-55 pt-4">
                        <button
                            type="submit"
                            disabled={savingDomain}
                            className="bg-gray-900 text-white px-8 py-3 rounded-xl font-bold hover:bg-gray-800 transition-all text-xs disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {savingDomain ? (
                                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                                'Save & Verify Domains'
                            )}
                        </button>
                    </div>

                    {/* Status Alerts */}
                    {customDomain && (
                        <div className="mt-3">
                            {domainVerified ? (
                                <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 text-xs font-semibold flex items-center gap-2">
                                    <Check className="w-4 h-4 text-green-600" />
                                    Domain verified and connected successfully! Your store is live on {customDomain}.
                                </div>
                            ) : (
                                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs font-semibold space-y-1">
                                    <div className="flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                                        <span>DNS Validation Pending: {dnsError || 'Verifying connection...'}</span>
                                    </div>
                                    <p className="text-gray-500 font-normal pl-6 mt-1">
                                        Please ensure your domain DNS settings are configured as shown below. It may take up to 24 hours for DNS changes to propagate globally.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Instruction guide */}
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-3">
                        <h4 className="text-xs uppercase font-black text-slate-500 tracking-wider">How to configure your DNS settings</h4>
                        <p className="text-xs text-slate-600">
                            Log in to your domain registrar (GoDaddy, Namecheap, etc.) and add one of the following DNS records:
                        </p>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs font-mono bg-white border border-slate-200 rounded-lg overflow-hidden">
                                <thead>
                                    <tr className="bg-slate-100 text-slate-600 font-bold border-b border-slate-200">
                                        <th className="p-2 border-r border-slate-200">Type</th>
                                        <th className="p-2 border-r border-slate-200">Host/Name</th>
                                        <th className="p-2">Value/Points To</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-b border-slate-200 text-slate-700">
                                        <td className="p-2 border-r border-slate-200">A</td>
                                        <td className="p-2 border-r border-slate-200">@</td>
                                        <td className="p-2 font-bold text-primary-600">76.76.21.21</td>
                                    </tr>
                                    <tr className="text-slate-700">
                                        <td className="p-2 border-r border-slate-200">CNAME</td>
                                        <td className="p-2 border-r border-slate-200">www</td>
                                        <td className="p-2 font-bold text-primary-600">cname.vercel-dns.com</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
